'use client';

import { Suspense } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { DatadogAppRouter, nextjsPlugin } from '@datadog/browser-rum-nextjs';
import { CONFIG_ENV, DATADOG_ENABLED } from '@m/config/env';
import { AppName } from '@m/types';

const ALLOWED_TRACING_URLS: {
  match: string;
  propagatorTypes: ('tracecontext' | 'datadog')[];
}[] = [
  { match: 'https://*.metropolis.io/*', propagatorTypes: ['tracecontext', 'datadog'] },
  { match: 'https://*.metrop.io/*', propagatorTypes: ['tracecontext', 'datadog'] },
];

export interface DatadogRumInitProps {
  service: AppName;
}

function rumConfigured() {
  return Boolean(
    DATADOG_ENABLED &&
    process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID &&
    process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  );
}

function initRum(service: AppName) {
  if (typeof window === 'undefined' || datadogRum.getInternalContext() || !rumConfigured()) {
    return;
  }

  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
  if (!applicationId || !clientToken) return;

  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service: `${service}-app`,
    env: CONFIG_ENV,
    version: process.env.NEXT_PUBLIC_RELEASE_TAG,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 10,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: ALLOWED_TRACING_URLS,
    // Manual views. createNextConfig injects onRouterTransitionStart so the
    // target URL is stored before location updates.
    plugins: [nextjsPlugin()],
  });
}

export default function DatadogRumInit({ service }: DatadogRumInitProps) {
  // DatadogAppRouter starts the view while rendering, so RUM has to be
  // initialized first. It reads the pathname from the root layout; Suspense
  // keeps that from blocking prerender of the rest of the page.
  initRum(service);

  if (!rumConfigured()) return null;

  return (
    <Suspense fallback={null}>
      <DatadogAppRouter />
    </Suspense>
  );
}
